import {
  Button,
  Dialog,
  Field,
  Input,
  Portal,
  Stack,
  Textarea,
} from '@chakra-ui/react'
import { AxiosError } from 'axios'
import { showSuccess } from '@/lib/alert'
import { useCreateAgenda } from '@/hooks/agendas/useCreateAgenda'
import { useUpdateAgenda } from '@/hooks/agendas/useUpdateAgenda'
import CustomDialogBackdrop from '@/components/CustomDialogBackdrop'
import type { ApiErrorResponse } from '@/lib/api'

type AgendaData = {
  id: string
  name: string
  description: string | null
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  agenda?: AgendaData
}

const toTitleCase = (str: string) =>
  str.trim().replace(/(?:^|\s)\S/g, (char) => char.toUpperCase())

const UpsertAgendaDialog = ({ open, onOpenChange, agenda }: Props) => {
  const isEditing = !!agenda

  const createMutation = useCreateAgenda()
  const updateMutation = useUpdateAgenda(agenda?.id ?? '')

  const isPending = createMutation.isPending || updateMutation.isPending

  const activeError = isEditing ? updateMutation.error : createMutation.error
  const validationErrors = (activeError as AxiosError<ApiErrorResponse> | null)?.response?.data?.validation_errors

  const handleClose = (open: boolean) => {
    if (!open) {
      createMutation.reset()
      updateMutation.reset()
    }
    onOpenChange(open)
  }

  const handleSubmit = (e: { preventDefault(): void; currentTarget: HTMLFormElement }) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    const name = toTitleCase(data.get('name') as string)
    const description = (data.get('description') as string).trim() || null

    if (isEditing) {
      updateMutation.mutate({ name, description }, {
        onSuccess: () => {
          showSuccess('Agenda atualizada com sucesso!')
          handleClose(false)
        },
      })
    } else {
      createMutation.mutate({ name, description }, {
        onSuccess: () => {
          showSuccess('Agenda criada com sucesso!')
          form.reset()
          handleClose(false)
        },
      })
    }
  }

  return (
    <Dialog.Root
      size="md"
      placement="center"
      open={open}
      onOpenChange={({ open }) => handleClose(open)}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content m={6}>
            <form onSubmit={handleSubmit}>
              <Dialog.Header>
                <Dialog.Title>{isEditing ? 'Editar agenda' : 'Nova agenda'}</Dialog.Title>
              </Dialog.Header>

              <Dialog.Body>
                <Stack gap="4">
                  <Field.Root required invalid={!!validationErrors?.name}>
                    <Field.Label>
                      Nome
                      <Field.RequiredIndicator />
                    </Field.Label>
                    <Input
                      name="name"
                      placeholder="Ex: Lagosta Bronzeada"
                      variant="subtle"
                      maxLength={150}
                      defaultValue={agenda?.name ?? ''}
                    />
                    {validationErrors?.name?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>

                  <Field.Root invalid={!!validationErrors?.description}>
                    <Field.Label>Descrição</Field.Label>
                    <Textarea
                      name="description"
                      placeholder="Descreva a agenda (opcional)"
                      rows={5}
                      variant="subtle"
                      maxLength={500}
                      defaultValue={agenda?.description ?? ''}
                    />
                    {validationErrors?.description?.map((error, index) => (
                      <Field.ErrorText key={index}>{error}</Field.ErrorText>
                    ))}
                  </Field.Root>
                </Stack>
              </Dialog.Body>

              <Dialog.Footer>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={isPending}
                >
                  Cancelar
                </Button>
                <Button size="sm" type="submit" colorPalette="primary" loading={isPending}>
                  {isEditing ? 'Salvar' : 'Criar agenda'}
                </Button>
              </Dialog.Footer>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export default UpsertAgendaDialog
